import { useState, useEffect, useMemo } from 'react';
import {
  CustomDeckORM,
  DocumentVersionORM,
  isCustomDeckORM,
  isFolderItemORM,
  Presentable,
} from 'src/types/types';
import { fetchHtmlManifests, fetchPagesJson } from 'src/utils/loadCloudfrontAsset/common';
import { detectArchivedFileKeyPath } from 'src/components/SlideSelector/useThumbnailSelector';
import logger from 'src/utils/logger';
import { getCurrentDocVer } from 'src/state/machines/sharing/shareUtils';
import {
  getContentPageWithPageSettingsHiddenState,
  getNormalizedPageSettingOverrides,
  getPageSettingOverridesByPageId,
} from 'src/utils/hiddenSlides';
import { useSelector } from 'src/state/redux';
import { selUserDocumentVersionSettingsMap } from 'src/state/redux/selector/document';
import useFeatureFlags from '../useFeatureFlags/useFeatureFlags';

export interface Recommendations {
  strategy?: string
  pageId: string
  score: number
}

// [TODO] - Extract this out into a higher, more visible level
export interface ContentPageData {
  pageId: string;
  content?: string;
  documentPageNumber: number;
  presentationPageNumber: number;
  enableSlideTextInsertion?: boolean;
  htmlUrl?: string;
  htmlPageImage?: string;
  speakerNotes?: string | null;
  s3Key: string;
  title?: string;
  summary?: string;
  documentSummary?: string;
  longSummary?: string;
  shortSummary?: string;
  recommendations: Recommendations[];
  mapping: string | null;
  originalTitle?: string;
  originalSpeakerNotes?: string;
  htmlMode?: 'classic' | 'spa';
  htmlEntryUrl?: string;
  isHidden?: boolean;
  sourceIsHidden?: boolean;
}

export interface UseContentPageDataOptions {
  disableCache?: boolean;
  pagesJsonNetworkFirst?: boolean;
  showOriginalData?: boolean;
  includeHiddenContentPages?: boolean;
}

interface ContentPageDataOptions extends Required<UseContentPageDataOptions> {
  htmlManifestNetworkFirst: boolean;
}

const getContentPageDataOptions = (
  disableCacheOrOptions?: boolean | UseContentPageDataOptions,
  showOriginalData?: boolean,
): ContentPageDataOptions => {
  if (typeof disableCacheOrOptions === 'object') {
    return {
      disableCache: disableCacheOrOptions.disableCache ?? false,
      pagesJsonNetworkFirst: disableCacheOrOptions.pagesJsonNetworkFirst ?? false,
      showOriginalData: disableCacheOrOptions.showOriginalData ?? false,
      includeHiddenContentPages: disableCacheOrOptions.includeHiddenContentPages ?? false,
      htmlManifestNetworkFirst: false,
    };
  }

  const disableCache = disableCacheOrOptions ?? false;

  return {
    disableCache,
    pagesJsonNetworkFirst: disableCache,
    showOriginalData: showOriginalData ?? false,
    includeHiddenContentPages: false,
    htmlManifestNetworkFirst: disableCache,
  };
};

/**
 * Custom hook to determine the strategy based on feature flags.
 * @returns {string} The selected strategy ('cosinesimil' or 'L2').
 */
export const useStrategy = (): string => {
  const featureFlags = useFeatureFlags();
  // Check local storage for override
  const localStorageOverride = localStorage.getItem('override_cosine_similarity');

  if (localStorageOverride === 'true') {
    return 'cosinesimil';
  }

  // Fallback to feature flag
  const strategy = featureFlags.BEAC_6770_use_cosine_similarity_for_slide_matching ? 'cosinesimil' : 'L2';
  return strategy;
};

// External cache to store fetched data
const cache: { [key: string]: ContentPageData[] } = {};

export const getDocumentSummariesFromContentPageDataCache = (
  documentVersionIds: string[],
): Record<string, string | undefined> => {
  const requestedDocumentVersionIds = new Set(documentVersionIds);

  return Object.values(cache).reduce<Record<string, string | undefined>>((summaries, contentPageData) => {
    contentPageData.forEach(page => {
      const documentVersionId = page.pageId.substring(0, page.pageId.lastIndexOf('_'));

      if (requestedDocumentVersionIds.has(documentVersionId) && !(documentVersionId in summaries)) {
        summaries[documentVersionId] = page.shortSummary;
      }
    });

    return summaries;
  }, {});
};

/**
 * Function for fetching the JSON file contents of a given presentable
 * @param presentable: Presentable
 * @param disableCacheOrOptions: boolean | UseContentPageDataOptions - Disables local cache or configures Pages.json fetch behavior.
 * @param showOriginalData: boolean - if true, originalTitle & originalSpeakerNotes will be in the return value if there is any.
 * @returns { matches: MatchedPage[], totalMatchCount: number }
 */
export const useContentPageData = (
  presentable?: Presentable,
  disableCacheOrOptions?: boolean | UseContentPageDataOptions,
  showOriginalDataOption?: boolean,
  includeHiddenContentPagesOption?: boolean,
) => {
  const {
    disableCache,
    htmlManifestNetworkFirst,
    includeHiddenContentPages,
    pagesJsonNetworkFirst,
    showOriginalData,
  } = getContentPageDataOptions(disableCacheOrOptions, showOriginalDataOption);
  const shouldIncludeHiddenContentPages = includeHiddenContentPages || !!includeHiddenContentPagesOption;
  const [contentPageData, setContentPageData] = useState<ContentPageData[] | undefined>(undefined);
  const strategy = useStrategy();
  const enableHiddenSlides = useFeatureFlags('BEAC_7666_enable_hidden_slides');
  const userDocumentVersionSettingsMap = useSelector(selUserDocumentVersionSettingsMap);
  const userPageSettingsByDocumentVersionId = useMemo(() => {
    if (!enableHiddenSlides) {
      return {};
    }

    const presentableORM = presentable?.orm;
    const customDeckId = isFolderItemORM(presentableORM) && isCustomDeckORM(presentableORM.relations.itemORM)
      ? presentableORM.relations.itemORM.model.id
      : undefined;
    const customDeckPageSettings = customDeckId
      ? userDocumentVersionSettingsMap.customDeckLevel[customDeckId]?.pageSettings
      : undefined;

    // Content JSON is cached per hidden-slide preference set so previews match the user's visibility.
    return presentable?.presentablePages.reduce<Record<string, ReturnType<typeof getNormalizedPageSettingOverrides>>>(
      (acc, page) => {
        const documentVersionId = page.documentVersionORM.model.id;
        const documentVersionPageSettings =
          userDocumentVersionSettingsMap.documentVersionLevel[documentVersionId]?.pageSettings;
        const pageSettings = customDeckPageSettings ?? documentVersionPageSettings ?? [];
        acc[documentVersionId] = getNormalizedPageSettingOverrides(pageSettings);
        return acc;
      },
      {},
    ) ?? {};
  }, [enableHiddenSlides, presentable, userDocumentVersionSettingsMap]);
  const userPageSettingsCacheKey = useMemo(() => Object
    .entries(userPageSettingsByDocumentVersionId)
    .map(([documentVersionId, pageSettings]) => {
      const settingsKey = pageSettings
        .map(pageSetting => `${pageSetting.pageId}:${pageSetting.hidden}`)
        .join(',');

      // Include documentVersionId because custom decks can combine pages from multiple versions.
      return `${documentVersionId}:${settingsKey}`;
    })
    .join('|'), [userPageSettingsByDocumentVersionId]);

  logger.contentPresenting.contentPageData.debug(
    '[START] Inside useContentPageData',
    { cacheKey: presentable?.orm.model.id },
  )

  // Fetches
  useEffect(() => {
    if (!presentable) return;

    // Create an abort controller to cancel async tasks if the component unmounts
    const controller = new AbortController();
    const { signal } = controller;
    // Check if data is already cached
    const cacheKey = `${presentable.orm.model.id}:${userPageSettingsCacheKey}`;
    logger.contentPresenting.contentPageData.debug('[UPDATE] inside useEffect', { cacheKey })

    if (cacheKey in cache && !disableCache) {
      logger.contentPresenting.contentPageData.debug('[END] Using cached data', { cacheKey });
      setContentPageData(cache[cacheKey]);
      return;
    }

    // Measure fetch time
    const fetchStartTime = performance?.now() ?? Date.now();
    logger.contentPresenting.contentPageData.debug('Start fetching page data...', { cacheKey })

    // Fetch the data and update the cache
    // [TODO] - Decouple this logic
    const getDocumentsTextsAndFormat = async () => {
      const s3Keys = presentable.presentablePages.reduce<{ [key: string]: string }>((acc, page) => {
        // since we are storing the titles and speaker notes in the json file, we need to make sure we are
        // getting the correct version of the file. If the file has been updated in the last 10 minutes,
        // we will use the current version of the file. Otherwise, we will use the version of the file
        // that was published at the time of the presentation. This is to ensure that the titles and
        // speaker notes are correct for the presentation, and avoid any caching issues (disk and cloudfront).
        const documentVersion = page.documentVersionORM

        if (!documentVersion) return acc
        if (documentVersion.model.conversionStatus !== 'PROCESSED') {
          // eslint-disable-next-line max-len
          logger.contentPresenting.contentPageData.verbose('documentVersion is not processed', { documentVersionId: documentVersion.model.id })
          return acc
        }

        if (!documentVersion.model.convertedFolderKey) return acc
        if (!page.documentVersionORM.model.convertedFolderKey) return acc
        const param = `?srcUpdatedAt=${page.documentVersionORM.model.updatedAt}`;
        acc[page.documentVersionORM.model.id] =
          `${page.documentVersionORM.model.convertedFolderKey}json/Pages.json${param}`;
        return acc;
      }, {});
      const htmlManifestS3Keys = presentable.presentablePages.reduce<{ [key: string]: string }>((acc, page) => {
        const documentVersion = page.documentVersionORM;

        if (
          !documentVersion ||
          documentVersion.model.conversionStatus !== 'PROCESSED' ||
          documentVersion.model.type !== 'HTML' ||
          !documentVersion.model.convertedFolderKey
        ) {
          return acc;
        }

        const { id, convertedFolderKey, updatedAt } = documentVersion.model;
        acc[id] = `${convertedFolderKey}html/manifest.json?srcUpdatedAt=${updatedAt}`;
        return acc;
      }, {});

      try {
        // Check if the abort signal is already triggered before starting
        if (signal.aborted) {
          setContentPageData([]);
          return;
        }

        const [responses, htmlManifestResponses] = await Promise.all([
          fetchPagesJson(s3Keys, pagesJsonNetworkFirst),
          fetchHtmlManifests(htmlManifestS3Keys, htmlManifestNetworkFirst),
        ]);

        if (!responses || signal.aborted) {
          setContentPageData([]);
          return;
        }

        const htmlManifestByDocumentVersionId = htmlManifestResponses.reduce<
          Record<string, { mode?: 'classic' | 'spa', entryHtmlUrl?: string }>
        >((acc, response) => {
          if (response.manifest) {
            acc[response.documentVersionId] = response.manifest;
          }

          return acc;
        }, {});
        const pages: ContentPageData[] = [];
        // ASSIGNS THE CONTENT TO EACH PAGE
        presentable.presentablePages.forEach((page) => {
          const documentVersion = page.documentVersionORM;
          const formattedPage: ContentPageData = {
            pageId: documentVersion.model.id + '_' + page.page.number,
            documentPageNumber: page.page.number,
            presentationPageNumber: page.presentationPageNumber,
            s3Key: detectArchivedFileKeyPath(documentVersion.model, page.page, 'sm') || '',
            recommendations: [],
            mapping: null,
          };

          // FINDS THE CONTENT FOR THE PAGE AND ASSIGNS IT
          responses.forEach((response) => {
            if (response.documentVersionId === page.documentVersionORM.model.id) {
              if (response.content.documentSummary !== undefined) {
                formattedPage.documentSummary = response.content.documentSummary;
              }
              formattedPage.longSummary = response.content.longSummary ?? undefined;
              formattedPage.shortSummary = response.content.shortSummary ?? undefined;
              formattedPage.htmlMode = htmlManifestByDocumentVersionId[response.documentVersionId]?.mode;
              formattedPage.htmlEntryUrl = htmlManifestByDocumentVersionId[response.documentVersionId]?.entryHtmlUrl;
              // FINDS THE PAGE
              response.content.pages.forEach((responsePage) => {
                if (responsePage?.number === page.page.number) {
                  formattedPage.speakerNotes = responsePage.overrideSpeakerNotes ?? responsePage.speakerNotes;
                  formattedPage.content = responsePage.content;
                  formattedPage.title = responsePage.overrideTitle ?? responsePage.title;
                  if (responsePage.summary !== undefined) {
                    formattedPage.summary = responsePage.summary;
                  }
                  formattedPage.htmlUrl = responsePage.htmlUrl;
                  formattedPage.htmlPageImage = responsePage.htmlPageImage;
                  const hasStrategyRecommendations = responsePage.recommendations?.some(
                    rec => rec.strategy === strategy,
                  );

                  formattedPage.recommendations = responsePage.recommendations?.filter(
                    rec => hasStrategyRecommendations
                      ? rec.strategy === strategy
                      : rec.strategy === null || rec.strategy === undefined,
                  ) ?? [];
                  formattedPage.enableSlideTextInsertion = !!responsePage.enableSlideTextInsertion;
                  formattedPage.mapping = responsePage.mapping ?? null;
                  formattedPage.originalTitle = showOriginalData ? responsePage.title : undefined;
                  formattedPage.originalSpeakerNotes = showOriginalData
                    ? responsePage.speakerNotes || undefined
                    : undefined;
                }
              });
            }
          });
          const pageWithSettingsHiddenState = getContentPageWithPageSettingsHiddenState({
            page: formattedPage,
            pageSettings: documentVersion.model.pageSettings,
          });
          const userHiddenOverride = getPageSettingOverridesByPageId(
            userPageSettingsByDocumentVersionId[documentVersion.model.id],
          )[pageWithSettingsHiddenState.pageId];
          // DocumentVersion supplies source metadata; user settings only change current visibility.
          const pageWithUserHiddenState = typeof userHiddenOverride === 'boolean'
            ? {
              ...pageWithSettingsHiddenState,
              isHidden: userHiddenOverride,
            }
            : pageWithSettingsHiddenState;

          pages.push(pageWithUserHiddenState);
        });

        cache[cacheKey] = pages;
        setContentPageData(pages);
      } catch (error) {
        if (!signal.aborted) {
          // Only log error if not aborted
          const errorText = 'Error fetching pages data';
          logger.contentPresenting.contentPageData.warn(errorText, { cacheKey, error });
        }
      }

      // Measure fetch time
      const fetchEndTime = performance.now();
      logger.contentPresenting.contentPageData.debug(
        `[END] Finished fetching page data. Fetch time: ${(fetchEndTime - fetchStartTime).toFixed(2)} ms`,
        { cacheKey },
      )
    };

    setContentPageData(undefined);
    getDocumentsTextsAndFormat();

    // Cleanup function to abort fetch on unmount
    return () => {
      controller.abort();
    };
  }, [
    presentable?.orm.model.id,
    // eslint-disable-next-line
    presentable?.orm.model['conversionStatus'], // this // eslint-disable-next-line is required due when we  try to fetch the data from the pages.json we need to guarantee that the document version is processed, if not, we will not be able to fetch the data from the pages.json processed and we will get empty recommendations
    presentable?.numberOfPages,
    disableCache,
    htmlManifestNetworkFirst,
    shouldIncludeHiddenContentPages,
    pagesJsonNetworkFirst,
    showOriginalData,
    strategy,
    userPageSettingsCacheKey,
  ]);

  const cacheKey = presentable ? `${presentable.orm.model.id}:${userPageSettingsCacheKey}` : undefined;
  if (cacheKey && cache[cacheKey] && !disableCache) {
    logger.contentPresenting.contentPageData.debug('[END] Using cached data', { cacheKey });
    return {
      contentPageData: cache[cacheKey],
    };
  }

  return {
    contentPageData: contentPageData || [],
  }
}

// [TODO] - A lot of these can probably be exposed at the ORM level
export const getSlideContentPageDataTitle = (
  idx: number,
  contentPageData?: ContentPageData[],
  showDefaultTitle: boolean = false,
): string => {
  const defaultTitle = showDefaultTitle ? `Slide ${idx + 1}` : '';
  if (!contentPageData || !contentPageData[idx]) return defaultTitle
  return contentPageData[idx].title || defaultTitle
}

export const hasSpeakerNotes = (idx: number, contentPageData?: ContentPageData[]): boolean => {
  if (!contentPageData || !contentPageData[idx]) return false
  return !!contentPageData[idx].speakerNotes
}

export const hasSlideTextInsertionEnabled = (idx: number, contentPageData?: ContentPageData[]): boolean => {
  if (!contentPageData || !contentPageData[idx]) return false
  return !!contentPageData[idx].enableSlideTextInsertion
}

// [TODO] - Expose this as part of the ORM object?
/**
 * this function generates all s3Keys of each document version within a custom deck
 * @param customDeckORM the custom deck to get all the s3Keys of all the document versions
 * @param latest boolean, if ture, return the latest version
 * @returns and object where each key is the docVerId from slides within custom deck and the value is the s3Key of that documentVersion
 */
export const getS3KeysWithinCustomDeck = (
  customDeckORM: CustomDeckORM,
  latest?: boolean,
): { [documentVersionId: string]: string } => {
  const s3Keys = customDeckORM.model.groups.reduce<{ [key: string]: string }>((acc, group) => {
    const documentVersionORM = getCurrentDocVer(group.pages[0].documentVersionId)

    const docVerORM = latest
      ? documentVersionORM?.relations.documentORM.relations.version.latestPublishedDocumentVersionORM
      : documentVersionORM

    if (!docVerORM) {
      return acc
    }

    if (docVerORM.model.convertedFolderKey && !acc[docVerORM.model.id]) {
      acc[docVerORM.model.id!] =
        `${docVerORM?.model.convertedFolderKey}json/Pages.json?srcUpdatedAt=${docVerORM.model.updatedAt}`
    }
    return acc
  }, {})

  return s3Keys
}

/**
 * this function generates all s3Keys of documentVersionORM
 * @param documentVersionORMs array of documentVersionORM
 * @returns and object where each key is the docVerId and the value is the s3Key of that documentVersion
 */
export const getS3KeysWithinDocumentVersionORM = (
  documentVersionORMs: DocumentVersionORM[],
): { [documentVersionId: string]: string } => {
  const s3Keys = documentVersionORMs.reduce<{ [key: string]: string }>((acc, docVerORM) => {
    if (docVerORM.model.convertedFolderKey && !acc[docVerORM.model.id]) {
      acc[docVerORM.model.id!] =
        `${docVerORM?.model.convertedFolderKey}json/Pages.json?srcUpdatedAt=${docVerORM.model.updatedAt}`
    }
    return acc
  }, {})

  return s3Keys
}

// [TODO] - Expose this as part of the ORM object?
// 3. Add the timestamp (10 minute for optimization) check to help avoid caching issues
// 4. (FUTURE) - Consider guaranteeing pages.json is fresh by
//               Creating a copy in a different directory that is actually frozen
export const getPageJSONS3KeyForDocVer = (docVerORM: DocumentVersionORM): string | undefined => {
  if (docVerORM.model.convertedFolderKey) {
    return `${docVerORM.model.convertedFolderKey}json/Pages.json?srcUpdatedAt=${docVerORM.model.updatedAt}`
  }
}

/**
 * Updates the enableSlideTextInsertion property for a specific page in the cache
 * @param cacheKey - The key used to identify the cached data
 * @param pageNumber - The page number to update (1-based index)
 * @param enableSlideTextInsertion - The new value for enableSlideTextInsertion
 */
export const updateEnableSlideTextInsertion = (
  cacheKey: string,
  pageNumber: number,
  enableSlideTextInsertion: boolean,
): void => {
  const pages = cache[cacheKey];
  if (pages) {
    const page = pages.find(p => p.presentationPageNumber === pageNumber);
    if (page) {
      page.enableSlideTextInsertion = enableSlideTextInsertion;
    }
    else {
      logger.contentPresenting.contentPageData.warn('page not found', { cacheKey, pageNumber });
    }
  }
  else {
    logger.contentPresenting.contentPageData.warn('pages not found', { cacheKey });
  }
};

export default useContentPageData;
